import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { type PopupData } from '../services/popupService'

interface PopupModalProps {
  popup: PopupData
  tenantId: string
  onClose: (popupId: string, dontShowAgain?: boolean) => void
}

const PopupModal: React.FC<PopupModalProps> = ({ popup, onClose }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [dontShowAgain, setDontShowAgain] = useState(false)

  const autoCloseTimeout = (popup as PopupData & { autoCloseTimeout?: number }).autoCloseTimeout || 0
  const shouldAutoClose = autoCloseTimeout > 0
  const isForceShow = popup.isForceShow || false
  const imagePosition = popup.imagePosition || 'top'
  const imageFit = popup.imageStyle === 'auto' ? 'contain' : popup.imageStyle || 'cover'

  const contentRef = useRef<HTMLDivElement | null>(null)

  const getPlainText = (html?: string): string => {
    if (!html) return ''
    return html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim()
  }

  const getRenderedHtml = (html?: string): string => {
    if (!html) return ''
    return /<[^>]+>/.test(html) ? html : html.replace(/\n/g, '<br />')
  }

  const displayMode = popup.displayMode || 'auto'
  const rawHasTitleText = Boolean((popup.title || '').trim())
  const rawHasContentText = Boolean(getPlainText(popup.content))
  const hasImage = Boolean(popup.imageUrl)
  const renderedContentHtml = getRenderedHtml(popup.content)
  const shouldRenderImage = hasImage && displayMode !== 'text_only'
  const shouldRenderText = displayMode === 'image_only' ? false : (rawHasTitleText || rawHasContentText)
  const hasTitleText = shouldRenderText && rawHasTitleText
  const hasContentText = shouldRenderText && rawHasContentText
  const hasTextualContent = hasTitleText || hasContentText
  const isImageTextMode =
    displayMode === 'image_text' ||
    (displayMode === 'auto' && shouldRenderImage && hasTextualContent)
  const topImageMaxHeight = isImageTextMode
    ? (hasContentText ? 'min(52vh, 440px)' : 'min(68vh, 580px)')
    : hasTextualContent
      ? 'min(32vh, 240px)'
      : '70vh'
  const bottomImageMaxHeight = isImageTextMode
    ? (hasContentText ? 'min(48vh, 400px)' : 'min(62vh, 520px)')
    : hasTextualContent
      ? 'min(28vh, 220px)'
      : '45vh'
  const sideImageMaxHeight = isImageTextMode
    ? (hasContentText ? 'min(48vh, 420px)' : 'min(62vh, 520px)')
    : hasTextualContent
      ? 'min(34vh, 280px)'
      : '360px'
  const centerImageMaxHeight = isImageTextMode
    ? (hasContentText ? 'min(50vh, 420px)' : 'min(66vh, 560px)')
    : hasContentText
      ? 'min(26vh, 210px)'
      : '260px'
  const accentColor = 'var(--htd-primary)'
  const contentPanelBackground = 'linear-gradient(135deg, var(--htd-primary) 0%, #0b6cb3 46%, var(--htd-secondary) 100%)'
  const contentPanelBorder = '1px solid rgba(125, 211, 252, 0.22)'
  const closeButtonTone: 'light' | 'dark' =
    shouldRenderImage && (!hasTextualContent || imagePosition === 'top' || imagePosition === 'background')
      ? 'dark'
      : 'light'

  useEffect(() => {
    const container = contentRef.current
    if (!container) return

    const mediaElements = container.querySelectorAll<HTMLElement>('img, video, iframe')
    mediaElements.forEach((el) => {
      el.style.maxWidth = '100%'
      el.style.height = 'auto'
      el.style.display = 'block'
      el.style.borderRadius = '12px'
    })
  }, [popup.content])

  useEffect(() => {
    const showTimer = window.setTimeout(() => setIsVisible(true), 100)

    if (shouldAutoClose) {
      setTimeRemaining(Math.ceil(autoCloseTimeout / 1000))
      const interval = window.setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev && prev <= 1) {
            handleClose()
            return null
          }
          return prev ? prev - 1 : null
        })
      }, 1000)

      return () => {
        window.clearTimeout(showTimer)
        window.clearInterval(interval)
      }
    }

    return () => window.clearTimeout(showTimer)
  }, [autoCloseTimeout, shouldAutoClose])

  const handleClose = () => {
    setIsVisible(false)
    window.setTimeout(() => {
      onClose(popup._id, dontShowAgain)
    }, 300)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && popup.allowDismiss) {
      handleClose()
    }
  }

  const renderTimer = (light: boolean) => {
    if (!shouldAutoClose || timeRemaining === null) return null
    return (
      <div
        className="mt-4 rounded-3 p-3"
        style={{
          background: light ? 'color-mix(in srgb, var(--htd-primary) 10%, transparent)' : 'rgba(255,255,255,0.15)',
          color: light ? '#334155' : '#ffffff',
        }}
      >
        <div className="text-center text-xs">ปิดอัตโนมัติใน {timeRemaining} วินาที</div>
        <div
          className="mt-2 h-1 w-full overflow-hidden rounded-full"
          style={{ background: light ? '#e2e8f0' : 'rgba(255,255,255,0.3)' }}
        >
          <div
            className="h-1 transition-all duration-1000"
            style={{
              width: `${(timeRemaining / (autoCloseTimeout / 1000)) * 100}%`,
              background: light ? accentColor : '#ffffff',
            }}
          />
        </div>
      </div>
    )
  }

  const renderDismissToggle = (light: boolean) => {
    if (!popup.allowDismiss || isForceShow) return null
    return (
      <div
        style={{
          marginTop: '18px',
          display: 'flex',
          justifyContent: light ? 'center' : 'flex-start',
        }}
      >
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            borderRadius: '14px',
            border: `1px solid ${light ? '#cbd5e1' : 'rgba(255,255,255,0.24)'}`,
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.2,
            color: light ? '#334155' : '#ffffff',
            background: light ? 'rgba(248,250,252,0.96)' : 'rgba(255,255,255,0.08)',
            boxShadow: light ? '0 8px 18px rgba(15,23,42,0.08)' : '0 10px 22px rgba(2,6,23,0.18)',
            userSelect: 'none',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              width: '16px',
              height: '16px',
              flex: '0 0 16px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '5px',
              border: `1.5px solid ${dontShowAgain
                ? (light ? accentColor : '#ffffff')
                : (light ? '#64748b' : 'rgba(255,255,255,0.72)')}`,
              background: dontShowAgain
                ? (light ? accentColor : '#ffffff')
                : 'transparent',
              color: dontShowAgain ? (light ? '#ffffff' : '#0f172a') : 'transparent',
              fontSize: '10px',
              fontWeight: 700,
              boxShadow: dontShowAgain ? '0 2px 8px color-mix(in srgb, var(--htd-primary) 28%, transparent)' : 'none',
              transition: 'all 160ms ease',
            }}
          >
            ✓
          </span>
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: '1px',
              height: '1px',
            }}
          />
          <span style={{ whiteSpace: 'nowrap' }}>ไม่ต้องแสดงอีก</span>
        </label>
      </div>
    )
  }

  const renderCloseButton = (tone: 'light' | 'dark' = 'dark') => (
    <button
      type="button"
      aria-label="Close"
      onClick={handleClose}
      style={{
        display: 'flex',
        width: '36px',
        height: '36px',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: '12px',
        right: '12px',
        zIndex: 12,
        borderRadius: '999px',
        borderWidth: '1px',
        borderStyle: 'solid',
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: 1,
        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.25)',
        color: tone === 'dark' ? '#ffffff' : '#0f172a',
        borderColor: tone === 'dark' ? 'rgba(255,255,255,0.78)' : 'rgba(15,23,42,0.14)',
        background: tone === 'dark' ? 'color-mix(in srgb, var(--htd-secondary) 82%, transparent)' : 'rgba(255,255,255,0.94)',
        backdropFilter: 'blur(10px)',
      }}
    >
      ×
    </button>
  )

  const renderHtmlContent = (light: boolean, centered: boolean = false) => (
    <div className={`${centered ? 'text-center' : 'text-left'}`}>
      {hasTitleText && (
        <h5
          className={`mb-3 text-2xl font-bold tracking-tight ${centered ? 'text-center' : ''}`}
          style={{ color: light ? accentColor : '#ffffff' }}
        >
          {popup.title}
        </h5>
      )}
      {hasContentText && (
        <div
          ref={contentRef}
          className={`text-[15px] leading-7 ${centered ? 'text-center' : ''}`}
          style={{ color: light ? '#334155' : 'rgba(255,255,255,0.95)' }}
          dangerouslySetInnerHTML={{ __html: renderedContentHtml }}
        />
      )}
      {renderTimer(light)}
      {renderDismissToggle(light)}
    </div>
  )

  const renderImage = (styleOverrides: React.CSSProperties = {}) => (
    <div style={{ position: 'relative', lineHeight: 0 }}>
      <img
        src={popup.imageUrl || ''}
        alt={popup.imageAlt || popup.title || 'popup image'}
        style={{
          width: '100%',
          maxWidth: '100%',
          objectFit: imageFit,
          display: 'block',
          ...styleOverrides,
        }}
      />
    </div>
  )

  const renderPromoHeroLayout = (options?: { titleOverlay?: boolean }) => {
    const titleOverlay = options?.titleOverlay ?? (hasTitleText && !hasContentText)
    const heroImageHeight = hasContentText ? 'min(52vh, 440px)' : 'min(68vh, 580px)'

    return (
      <div>
        <div style={{ position: 'relative', overflow: 'hidden', background: '#0a1638' }}>
          {renderImage({ maxHeight: heroImageHeight, minHeight: titleOverlay ? '220px' : undefined })}
          {titleOverlay && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                padding: '28px 24px 22px',
                background: 'linear-gradient(180deg, rgba(10,22,56,0) 0%, rgba(10,22,56,0.55) 42%, rgba(10,22,56,0.92) 100%)',
              }}
            >
              <h5
                className="text-center text-2xl font-bold tracking-tight"
                style={{ color: '#ffffff', margin: 0, textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}
              >
                {popup.title}
              </h5>
            </div>
          )}
        </div>

        {!titleOverlay && (
          <div
            style={{
              padding: '20px 24px',
              background: contentPanelBackground,
              color: '#ffffff',
              borderTop: contentPanelBorder,
            }}
          >
            {renderHtmlContent(false, true)}
          </div>
        )}

        {titleOverlay && (
          <div style={{ padding: '16px 24px 20px', background: '#ffffff' }}>
            {renderTimer(true)}
            {renderDismissToggle(true)}
          </div>
        )}
      </div>
    )
  }

  const renderBody = () => {
    if (shouldRenderImage && !hasTextualContent) {
      return renderImage({ maxHeight: '70vh', borderRadius: '20px' })
    }

    if (!shouldRenderImage) {
      return (
        <div
          style={{
            padding: '24px',
            background: contentPanelBackground,
            color: '#ffffff',
          }}
        >
          {renderHtmlContent(false, true)}
        </div>
      )
    }

    if (imagePosition === 'background') {
      return (
        <div
          className="relative overflow-hidden"
          style={{
            minHeight: '420px',
            maxHeight: 'calc(78vh - 24px)',
            backgroundImage: `linear-gradient(rgba(0,76,61,0.42), rgba(0,76,61,0.76)), url("${popup.imageUrl}")`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: imageFit === 'fill' ? '100% 100%' : imageFit,
          }}
        >
          <div className="relative z-10 p-6">{renderHtmlContent(false, true)}</div>
        </div>
      )
    }

    if (imagePosition === 'left' || imagePosition === 'right') {
      return (
        <div className="flex flex-col md:flex-row" style={{ flexDirection: imagePosition === 'right' ? 'row-reverse' : 'row' }}>
          <div className="md:w-2/5">{renderImage({ height: '100%', minHeight: '220px', maxHeight: sideImageMaxHeight })}</div>
          <div className="flex-1 p-6" style={{ background: contentPanelBackground, color: '#ffffff' }}>{renderHtmlContent(false)}</div>
        </div>
      )
    }

    if (imagePosition === 'bottom') {
      return (
        <>
          <div className="p-6" style={{ background: contentPanelBackground, color: '#ffffff' }}>{renderHtmlContent(false)}</div>
          {renderImage({ maxHeight: bottomImageMaxHeight })}
        </>
      )
    }

    if (imagePosition === 'center') {
      if (isImageTextMode) {
        return renderPromoHeroLayout({ titleOverlay: hasTitleText && !hasContentText })
      }

      return (
        <div style={{ padding: '24px', background: contentPanelBackground, color: '#ffffff' }}>
          {hasTitleText && (
            <h5 className="mb-4 text-center text-2xl font-bold" style={{ color: '#ffffff' }}>
              {popup.title}
            </h5>
          )}
          {renderImage(
            {
              maxHeight: centerImageMaxHeight,
              width: imageFit === 'contain' ? 'auto' : '100%',
              margin: '0 auto 20px',
              borderRadius: '16px',
            },
          )}
          {hasContentText && (
            <div
              ref={contentRef}
              className="text-center text-[15px] leading-7 text-slate-700"
              style={{ color: 'rgba(255,255,255,0.95)' }}
              dangerouslySetInnerHTML={{ __html: renderedContentHtml }}
            />
          )}
          {renderTimer(false)}
          {renderDismissToggle(false)}
        </div>
      )
    }

    if (isImageTextMode && (imagePosition === 'top' || !imagePosition)) {
      return renderPromoHeroLayout()
    }

    return (
      <>
        {renderImage({ maxHeight: topImageMaxHeight })}
        <div
          style={{
            padding: '24px',
            background: contentPanelBackground,
            color: '#ffffff',
            borderTop: contentPanelBorder,
          }}
        >
          {renderHtmlContent(false, true)}
        </div>
      </>
    )
  }

  return createPortal(
    <div
      className={`fixed inset-0 flex items-start justify-center transition-all duration-300 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'} md:items-center`}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1050,
        backgroundColor: 'rgba(2, 6, 23, 0.78)',
        backdropFilter: 'blur(6px)',
        paddingTop: '40px',
        paddingBottom: '40px',
        overflowY: 'auto',
      }}
    >
      <div
        className={`relative transform transition-all duration-300 ease-out ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 -translate-y-8'} mx-auto mt-4 flex flex-col overflow-hidden rounded-4 bg-white shadow-lg md:mt-0`}
        style={{
          position: 'relative',
          maxWidth:
            shouldRenderImage && hasTextualContent && (imagePosition === 'left' || imagePosition === 'right' || imagePosition === 'background')
              ? '760px'
              : shouldRenderImage
                ? 'min(92vw, 680px)'
                : '420px',
          width: '90%',
          maxHeight: 'calc(90vh - 80px)',
          zIndex: 1060,
          border: `1px solid rgba(125, 211, 252, 0.35)`,
          borderRadius: '24px',
          background: '#ffffff',
          boxShadow: '0 30px 80px rgba(2, 6, 23, 0.48), 0 0 0 1px color-mix(in srgb, var(--htd-primary) 12%, transparent)',
          margin: '0 auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {popup.allowDismiss ? renderCloseButton(closeButtonTone) : null}

        <div className="overflow-y-auto p-0" style={{ maxHeight: 'calc(85vh - 20px)' }}>
          {renderBody()}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default PopupModal

